import { Link } from 'react-router-dom';

interface NewsCardProps {
  id: number;
  title: string;
  excerpt: string;
  imageUrl: string;
  author: string;
  publishDate: string;
  readCount: number;
  tags: string[];
  variant?: 'horizontal' | 'vertical';
  size?: 'small' | 'large';
}

export function NewsCard({
  id,
  title,
  excerpt,
  imageUrl,
  author,
  publishDate,
  readCount,
  tags,
  variant = 'vertical',
  size = 'large',
}: NewsCardProps) {
  if (variant === 'horizontal') {
    return (
      <article className="flex flex-col sm:flex-row gap-4 p-4 bg-background-100 rounded-lg hover:bg-background-200/70 transition-colors duration-200 group">
        <Link to="/" className="flex-shrink-0 w-full sm:w-48 h-36 rounded-md overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </Link>
        <div className="flex flex-col justify-between flex-1 min-w-0">
          <div>
            <Link to="/">
              <h3 className="text-base font-heading font-bold text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 mb-2">
                {title}
              </h3>
            </Link>
            <Link to="/" className="block">
              <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">{excerpt}</p>
            </Link>
          </div>
          <div className="flex items-center gap-4 mt-3 flex-wrap">
            <div className="flex flex-wrap gap-1.5">
              {tags.slice(0, 2).map((tag) => (
                <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-primary-500/15 text-primary-400 whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
            <span className="text-xs text-foreground-500 whitespace-nowrap">{publishDate}</span>
            <span className="text-xs text-foreground-500 whitespace-nowrap">
              <i className="ri-eye-line mr-1"></i>{(readCount / 10000).toFixed(1)}万
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <Link to="/" className="block h-full">
      <article className="bg-background-100 rounded-lg overflow-hidden hover:bg-background-200/70 transition-colors duration-200 group h-full flex flex-col">
        <div className="flex-shrink-0">
          <div className={`${size === 'large' ? 'aspect-[4/3]' : 'aspect-[3/2]'} overflow-hidden`}>
            <img
              src={imageUrl}
              alt={title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          </div>
        </div>
        <div className="p-4 flex flex-col flex-1">
          <div className="flex-1">
            <h3 className={`font-heading font-bold text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 ${size === 'large' ? 'text-base' : 'text-sm'} mb-2`}>
              {title}
            </h3>
          </div>
          {size === 'large' && (
            <p className="text-sm text-foreground-600 line-clamp-2 mb-3 leading-relaxed">{excerpt}</p>
          )}
          <div className="flex items-center justify-between mt-auto">
            <div className="flex flex-wrap gap-1.5">
              {tags.slice(0, 2).map((tag) => (
                <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-primary-500/15 text-primary-400 whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
            <span className="text-xs text-foreground-500 whitespace-nowrap">{publishDate}</span>
          </div>
        </div>
      </article>
    </Link>
  );
}